import * as THREE from "three";
import { fabricTexture } from "../textures";
import { inked, toon, type ToyParams } from "../toon";
import { facets } from "./geom";

// The rider's shared materials and limb pieces.

/** Soft skin: a little glossier than cloth, with a warm rim. */
export function skinMat(color: string): THREE.MeshStandardMaterial {
  return toon(color, { roughness: 0.5 }, "skin");
}

let weave: THREE.Texture | null | undefined;
let twill: THREE.Texture | null | undefined;

/** Matte cloth with a fine weave (or denim twill) in the color. */
export function cloth(color: string, denim = false, extra: ToyParams = {}): THREE.MeshStandardMaterial {
  if (denim) twill ??= fabricTexture(true);
  else weave ??= fabricTexture(false);
  const map = denim ? twill : weave;
  return toon(color, { roughness: 0.85, ...(map ? { map } : {}), ...extra }, denim ? "denim" : "cloth");
}

/** Leggings (#227): smooth stretch fabric, a little sheen, no denim twill. */
export function stretch(color: string): THREE.MeshStandardMaterial {
  return cloth(color, false, { roughness: 0.55 });
}

/** What the bottoms' pelvis is made of: denim for cut-offs, stretch for leggings, else plain cloth (pants, baggy). */
export function bottomsCloth(bottoms: { style: string; color: string }): THREE.MeshStandardMaterial {
  return bottoms.style === "leggings" ? stretch(bottoms.color) : cloth(bottoms.color, bottoms.style === "cutoffs");
}

export function capsule(radius: number, length: number, mat: THREE.Material): THREE.Mesh {
  const g = new THREE.CapsuleGeometry(radius, length, facets(8, 1), facets(16));
  g.translate(0, -length / 2 - radius * 0.3, 0); // hang down from the pivot
  return inked(g, mat);
}

/**
 * One limb bone as a tapered tube (#75, the realistic build), lathed so it
 * costs one draw: from the pivot (y 0, radius `top`) down to the next joint
 * (y -`length`, radius `bottom`), with a hemisphere on each end centered on
 * the joint. The tube meeting it at that joint starts with the same radius,
 * so the bend is one smooth round, not a ball between two capsules.
 * `swell` widens it by that fraction at `at` (0 top, 1 bottom): a bicep, a
 * forearm, a calf.
 */
export function limb(top: number, bottom: number, length: number, mat: THREE.Material, swell = 0, at = 0.4): THREE.Mesh {
  const pts: THREE.Vector2[] = [];
  const CAP = 6;
  // Bottom cap: from the tip below the joint up to its equator.
  for (let i = 0; i < CAP; i++) {
    const a = -Math.PI / 2 + (i / CAP) * (Math.PI / 2);
    pts.push(new THREE.Vector2(Math.cos(a) * bottom, -length + Math.sin(a) * bottom));
  }
  pts.push(...limbProfile(top, bottom, length, swell, at));
  // Top cap: from the pivot's equator up to the tip.
  for (let i = 1; i <= CAP; i++) {
    const a = (i / CAP) * (Math.PI / 2);
    pts.push(new THREE.Vector2(Math.max(Math.cos(a) * top, 1e-4), Math.sin(a) * top));
  }
  return inked(new THREE.LatheGeometry(pts, 16), mat);
}

/**
 * A limb's side, bottom to top, without the rounded ends: a straight taper
 * with a smooth swell that's zero at both joints. Also shapes the tattoo
 * sleeve over a tapered arm.
 */
export function limbProfile(top: number, bottom: number, length: number, swell = 0, at = 0.4, steps = 12): THREE.Vector2[] {
  const pts: THREE.Vector2[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = 1 - i / steps; // 1 at the bottom joint, 0 at the pivot
    pts.push(new THREE.Vector2(limbRadius(top, bottom, t, swell, at), -length * t));
  }
  return pts;
}

/** A tapered limb's radius at `t` of the way down it (0 at the pivot, 1 at the next joint): what a patch wraps round. */
export function limbRadius(top: number, bottom: number, t: number, swell = 0, at = 0.4): number {
  const bump = t <= at ? Math.sin((t / at) * (Math.PI / 2)) : Math.cos(((t - at) / (1 - at)) * (Math.PI / 2));
  return (top + (bottom - top) * t) * (1 + swell * bump * bump);
}

/** The cloth weaves every rider shares, which `disposeRider` must keep. */
export function sharedWeave(t: THREE.Texture): boolean {
  return t === weave || t === twill;
}
